/** @file solveTwoBone: analytic two-bone IK keeping both lengths and the bend side. */
import { clamp } from '@zilo/pose3d-std/math/clamp';
import { quatRotateVec3 } from '@zilo/pose3d-math/quat/quatRotateVec3';
import type { Vec3 } from '@zilo/pose3d-math/vec3/vec3';
import { vec3Add } from '@zilo/pose3d-math/vec3/vec3Add';
import { vec3Length } from '@zilo/pose3d-math/vec3/vec3Length';
import { vec3Scale } from '@zilo/pose3d-math/vec3/vec3Scale';
import { vec3Sub } from '@zilo/pose3d-math/vec3/vec3Sub';
import { ikAxisAngle } from '@zilo/pose3d-pose/_internal/ikAxisAngle';
import { ikCross } from '@zilo/pose3d-pose/_internal/ikCross';
import { ikDot } from '@zilo/pose3d-pose/_internal/ikDot';
import { ikNormalize } from '@zilo/pose3d-pose/_internal/ikNormalize';
import type { IkInput, IkResult } from '@zilo/pose3d-pose/ik/ikTypes';

const DEGENERATE = 1e-6;
const TOWARD_CAMERA: Vec3 = [0, -1, 0];

/** Unit `v` with its component along unit `d` removed, or null when nothing is left. */
function perpTo(v: Vec3, d: Vec3): Vec3 | null {
  const p = vec3Sub(v, vec3Scale(d, ikDot(v, d)));
  return vec3Length(p) < DEGENERATE ? null : ikNormalize(p);
}

/**
 * Points `[A, B, E]` and target T: `d = clamp(|T - A|, |l1 - l2|, l1 + l2)`, `E' = A + dir(T - A) d`, and `B'` is
 * `dir(T - A)` turned by `acos((l1^2 + d^2 - l2^2) / (2 l1 d))` about the bend normal toward the side B was on
 * (normal `(E - A) x (B - A)`, made perpendicular to the target direction; straight chains bend toward the camera).
 */
export function solveTwoBone(input: IkInput, out: IkResult): IkResult {
  const [a, b, e] = input.points as [Vec3, Vec3, Vec3];
  const t = input.target;
  const l1 = vec3Length(vec3Sub(b, a));
  const l2 = vec3Length(vec3Sub(e, b));
  const toT = vec3Sub(t, a);
  const reach = vec3Length(toT);
  const d = clamp(reach, Math.abs(l1 - l2) + 1e-9, l1 + l2 - 1e-9);
  const dir = reach < DEGENERATE ? ikNormalize(vec3Sub(e, a)) : ikNormalize(toT);
  const n = perpTo(ikCross(vec3Sub(e, a), vec3Sub(b, a)), dir) ?? perpTo(ikCross(dir, TOWARD_CAMERA), dir) ?? perpTo([1, 0, 0], dir) ?? [0, 1, 0];
  const cosA = clamp((l1 * l1 + d * d - l2 * l2) / (2 * l1 * d), -1, 1);
  const knee = vec3Add(a, vec3Scale(quatRotateVec3(ikAxisAngle(n, Math.acos(cosA)), dir), l1));
  out.points = [a, knee, vec3Add(a, vec3Scale(dir, d))];
  out.reached = reach <= l1 + l2;
  return out;
}
